import { ButtonMenuProps } from "./types";
import { Children, isValidElement, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import ButtonGroup from "./ButtonGroup";

/** Razmak između gumba i izbornika ispod njega. */
const MENU_OFFSET = 8;

const ButtonMenu: React.FC<ButtonMenuProps> = ({
  startIcon,
  endIcon,
  text,
  children,
  cssClassName,
  size,
  variant = "text",
  groupDirection = "vertical"
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  const isHorizontal = groupDirection === "horizontal";

  // Za vodoravni raspored djecu (ravni popis: grupa + itemi) grupiramo po ButtonGroup-u
  // pa svaku grupu renderamo kao zasebnu kolonu jednu do druge.
  const renderContent = () => {
    if (!isHorizontal) {
      return <div className="flex flex-col">{children}</div>;
    }

    const groups: React.ReactNode[][] = [];
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.type === ButtonGroup) {
        groups.push([child]);
      } else {
        if (groups.length === 0) groups.push([]);
        groups[groups.length - 1].push(child);
      }
    });

    return (
      <div className="flex divide-x divide-gray-100">
        {groups.map((group, index) => (
          <div key={index} className="flex flex-col min-w-[12rem]">
            {group}
          </div>
        ))}
      </div>
    );
  };

  const handleOpen = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (!open) {
        // Položaj se čita iz gumba u trenutku otvaranja; izbornik se veže uz njegov desni rub.
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

        setPosition({
          top: rect.bottom + MENU_OFFSET,
          right: window.innerWidth - rect.right,
        });
      }

      setOpen(!open);
    },
    [open]
  );

  // Skrolanje i promjena veličine pomiču gumb, a izbornik bi ostao visjeti na starom mjestu —
  // jednostavnije ga je zatvoriti nego neprestano premještati.
  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const handleBlur = useCallback(
    (e: any) => {
      setTimeout(() => {
        e.preventDefault();
        setOpen(false);
      }, 500);
    },
    [open]
  );

  const sizeClassName = () => {
    switch (size) {
      case "small":
        return "px-3 py-1.5 text-sm";
      case "medium":
        return "px-3 py-2";
      case "large":
        return "px-3.5 py-3";
      case "full":
        return "w-full px-3 py-3 justify-center";
      default:
        return "w-max px-1 py-1";
    }
  };

  const variantClassName = () => {
    switch (variant) {
      case "text":
        return "text-blue-500";
      case "outlined":
        return "ring-1 ring-slate-300  hover:bg-blue-600 hover:ring-blue-900 hover:text-white transition ease-in-out delay-150";
      case "primary":
        return "bg-blue-500 text-white hover:bg-blue-600 hover:text-white transition ease-in-out delay-150";
      case "secondary":
        return "bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition ease-in-out delay-150";
     case "remove":
        return "bg-red-200 text-red-600 hover:bg-red-500 hover:text-white transition ease-in-out delay-150";
      default:
        return "";
    }
  };

  const iconClassName = () => {
    switch (variant) {
      case "text":
        return "stroke-[#181b31]";
      case "outlined":
        return "stroke-[#181b31] group-hover:stroke-white";
      case "primary":
        return "stroke-white group-hover:stroke-white";
      case "secondary":
        return "stroke-blue-800 group-hover:stroke-white";
      case "remove":
        return "stroke-[#fff] group-hover:stroke-white";
      default:
        return "";
    }
  };

  const commonClassName = () => {
    return classNames("py-1.5 group inline-flex w-full justify-center items-center  font-semibold text-slate-600 focus:outline-none transition ease-in-out delay-150", !text ? "rounded-full" : "rounded-sm");
  };

  return (
    <>
      <div className="relative inline-block float-right text-left">
        <button
          type="button"
          className={classNames(
            startIcon ? "px-1.5" : "",
            endIcon ? "px-1.5" : "",
            commonClassName(),
            sizeClassName(),
            variantClassName(),
            cssClassName
          )}
          id="menu-button"
          aria-expanded="true"
          aria-haspopup="true"
          onClick={(e) => handleOpen(e)}
          onBlur={(e) => handleBlur(e)}
        >
          {startIcon && (
            <ReactSVG
              src={startIcon}
              className={classNames(
                iconClassName(),
                "w-[14px] h-[14px] transition ease-in-out delay-150"
              )}
            />
          )}
          {text && <span className="ml-0.5 mr-0.5 inline-block text-sm">{text}</span>}

          {endIcon && (
            <ReactSVG
              src={endIcon}
              className={classNames(
                iconClassName(),
                "w-[14px] h-[14px] ml-0.5 transition ease-in-out delay-150"
              )}
            />
          )}
        </button>
        {/* Izbornik ide u portal jer bi ga inače odrezao svaki roditelj s `overflow` — a tablice
            se na užem ekranu moraju moći pomicati vodoravno. Položaj se računa iz gumba. */}
        {open && position && ReactDOM.createPortal(
          <div
            className={classNames(
              isHorizontal ? "w-max" : "w-56",
              "fixed z-[70] origin-top-right rounded-sm bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            )}
            style={{ top: position.top, right: position.right }}
          >
            {renderContent()}
          </div>,
          document.body
        )}
      </div>
    </>
  );
};

export default ButtonMenu;
